import type { BondFormTemplate } from "@portal/api/bond-request/agent";
import type { FC } from "react";
import { bondFormTypeLabelMap } from "../../../../constants";
import { ConfirmationModalClickWrapper } from "../../../layout/ConfirmationModalClickWrapper";
import { cn } from "../../../lib/utils";
import { ClickableIcon, Icon, Text } from "../../../primitives";
import { TruncatedLabel } from "../../../ui/TruncatedLabel";

export type SelectedBondFormTemplateCardProps = {
	template: BondFormTemplate;
	compact?: boolean;
	onRemove?: () => void;
};

export const SelectedBondFormTemplateCard: FC<SelectedBondFormTemplateCardProps> = ({
	template,
	compact = false,
	onRemove,
}) => {
	const typeLabel = `${bondFormTypeLabelMap[template.type]} Bond`;

	return (
		<div
			data-testid="selected-bond-form-template"
			className={cn(
				"flex items-center bg-surface-base border border-bc-default",
				compact
					? "gap-3 rounded-md px-3 py-2.5"
					: "min-h-[50px] w-[450px] break-words space-x-[25px] p-[15px] rounded-[5px] shadow-sm",
			)}
		>
			<Icon type="book-open" size={compact ? "mini" : "medium"} color="secondary" />
			{compact ? (
				<div className="flex min-w-0 flex-1 items-baseline gap-2">
					<TruncatedLabel
						label={template.name}
						className="min-w-0 text-[14px] leading-[20px] text-secondary"
					/>
				</div>
			) : (
				<div className="flex-1 flex flex-col gap-2.5">
					<Text type="body-strong" color="primary">
						{template.name}
					</Text>
					<Text type="caption" color="tertiary">
						{typeLabel}
					</Text>
				</div>
			)}
			{onRemove && (
				<ConfirmationModalClickWrapper
					message={
						<div className="w-[375px] text-center break-words">
							Are you sure you want to remove
							<br />
							{template.name}?
						</div>
					}
					className="items-center"
					confirmButtonText="Remove"
					onConfirm={onRemove}
				>
					<ClickableIcon
						type="x-mark"
						size={compact ? "mini" : "small"}
						color="tertiary"
						aria-label="Remove bond form"
						testId="deselect-bond-form-template"
					/>
				</ConfirmationModalClickWrapper>
			)}
		</div>
	);
};
